import { useId } from 'react';
import { Button, Stack, Tag } from '@carbon/react';
import { TrashCan } from '@carbon/icons-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { AnnouncementResponse } from '@sphere/communication/domain';
import styles from './AnnouncementCard.module.scss';

export interface AnnouncementCardProps {
  announcement: AnnouncementResponse;
  onDelete?: (id: string) => void;
  isDeletePending?: boolean;
}

function priorityTag(priority: string) {
  switch (priority) {
    case 'URGENT':
      return <Tag type="red">Urgent</Tag>;
    case 'HIGH':
      return <Tag type="blue">Important</Tag>;
    case 'LOW':
      return <Tag type="gray">Info</Tag>;
    default:
      return <Tag type="outline">Normal</Tag>;
  }
}

export function AnnouncementCard({
  announcement,
  onDelete,
  isDeletePending = false,
}: AnnouncementCardProps) {
  const titleId = useId();
  const isUrgent = announcement.priority === 'URGENT';

  const relativeTime = formatDistanceToNow(new Date(announcement.createdAt), {
    addSuffix: true,
    locale: fr,
  });

  return (
    <article
      className={`${styles.card} ${isUrgent ? styles.cardUrgent : ''}`}
      aria-labelledby={titleId}
    >
      <Stack gap={4}>
        <div className={styles.headRow}>
          <div className={styles.headMain}>
            <div className={styles.titleLine}>
              <h3 id={titleId} className={styles.title}>
                {announcement.title}
              </h3>
              {priorityTag(announcement.priority)}
              {!announcement.isRead && <Tag type="blue">Non lu</Tag>}
            </div>
            <p className={styles.meta}>
              <span>Par {announcement.authorName}</span>
              <span className={styles.metaSep} aria-hidden>
                ·
              </span>
              <time dateTime={new Date(announcement.createdAt).toISOString()}>
                {relativeTime}
              </time>
              {announcement.classId && (
                <>
                  <span className={styles.metaSep} aria-hidden>
                    ·
                  </span>
                  <Tag type="gray">Classe spécifique</Tag>
                </>
              )}
              {announcement.targetRoles?.length ? (
                <>
                  <span className={styles.metaSep} aria-hidden>
                    ·
                  </span>
                  <Tag type="gray">
                    {announcement.targetRoles.join(', ')}
                  </Tag>
                </>
              ) : null}
            </p>
          </div>
          {announcement.capabilities?.canDelete && onDelete && (
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={`Supprimer l'annonce « ${announcement.title} »`}
              className={styles.deleteBtn}
              disabled={isDeletePending}
              onClick={event => {
                event.stopPropagation()
                onDelete(announcement.id)
              }}
              renderIcon={TrashCan}
              tooltipPosition="left"
            />
          )}
        </div>
        <div className={styles.body}>{announcement.content}</div>
      </Stack>
    </article>
  );
}
